import { Form } from 'antd';
import { CategorySelector } from '../../../ui/Select/Selectors/CategorySelectors';
import { TransactionTypeSelector } from '../../../ui/Select/Selectors/TransactionTypeSelector';
import { StatusCheckboxes } from '../../../ui/Checkbox/Checkboxes/StatusCheckboxes';
import { Input, Button } from '../../../ui';
import { useEffect, useRef } from 'react';
import {
  ListingFilters as ListingFiltersType,
  ListingFilterOptions,
} from './types';
import { useIntl } from 'react-intl';
import { LocationRadiusSelectorWithAmk } from '../../../ui/Select/Selectors/LocationRadiusSelectorWithAmk';
import { useAvailableFilterOptions } from '../../../utils/useAvailableFilterOptions';
import { StyledListingsFilters } from './style';

interface Props {
  setFilter: (newFilter: ListingFiltersType) => void;
  filterOptions: ListingFilterOptions;
  filter: ListingFiltersType;
}

export const ListingsFilters = ({
  setFilter,
  filterOptions,
  filter,
}: Props) => {
  const [form] = Form.useForm();
  const filterCategory: ListingFiltersType = { ...filter };
  const intl = useIntl();
  const prevMainCategoryRef = useRef<number | null | undefined>(
    filterCategory['main-category']
  );

  const { availableOptions } = useAvailableFilterOptions(filter);

  useEffect(() => {
    if (!availableOptions) return;

    const currentValues = form.getFieldsValue();
    const updates: Partial<ListingFiltersType> = {};

    if (currentValues.transaction_type &&
        !availableOptions.transaction_types.includes(currentValues.transaction_type)) {
      updates.transaction_type = undefined;
    }

    if (currentValues.status && Array.isArray(currentValues.status)) {
      const validStatuses = currentValues.status.filter((s: string) =>
        availableOptions.statuses.includes(s)
      );
      if (validStatuses.length !== currentValues.status.length) {
        updates.status = validStatuses.length > 0 ? validStatuses : undefined;
      }
    }

    if (currentValues['main-category']) {
      const isValid = availableOptions.main_categories.some(
        cat => cat.id === currentValues['main-category']
      );
      if (!isValid) {
        updates['main-category'] = undefined;
        updates['sub-category'] = undefined;
        updates['sub-subcategory'] = undefined;
      }
    }

    if (currentValues['sub-category']) {
      const isValid = availableOptions.sub_categories.some(
        cat => cat.id === currentValues['sub-category']
      );
      if (!isValid) {
        updates['sub-category'] = undefined;
        updates['sub-subcategory'] = undefined;
      }
    }

    if (currentValues['sub-subcategory']) {
      const isValid = availableOptions.sub_subcategories.some(
        cat => cat.id === currentValues['sub-subcategory']
      );
      if (!isValid) {
        updates['sub-subcategory'] = undefined;
      }
    }

    if (Object.keys(updates).length > 0) {
      form.setFieldsValue(updates);
      setFilter({ ...currentValues, ...updates });
    }
  }, [availableOptions]);

  useEffect(() => {
    if (!!filter) {
      form.setFieldsValue(filter);
    }
  }, [form, filter]);

  useEffect(() => {
    const currentMainCategory = filterCategory['main-category'];
    const prevMainCategory = prevMainCategoryRef.current;

    if (prevMainCategory && !currentMainCategory) {
      form.setFieldsValue({
        'sub-category': null,
        'sub-subcategory': null,
      });
      setFilter({
        ...filter,
        'sub-category': null,
        'sub-subcategory': null,
      });
    }

    prevMainCategoryRef.current = currentMainCategory;
  }, [filterCategory['main-category']]);

  const handleClearAllFilters = () => {
    form.resetFields();
    setFilter({});
  };

  const handleValuesChange = (_changedValues: unknown, allValues: unknown) => {
    const values = allValues as ListingFiltersType;
    const searchValue = values.search;

    if (searchValue && searchValue.length > 0 && searchValue.length < 3) {
      return;
    }

    setFilter(values);
  };

  return (
    <StyledListingsFilters className={'form-container'}>
      <Form layout={'vertical'} onValuesChange={handleValuesChange} form={form}>
        <Input
          name="search"
          label={intl.formatMessage({ id: 'listing.filter.search' })}
          allowClear
        />

        <TransactionTypeSelector
          availableOptions={availableOptions?.transaction_types}
        />

        <div className="category-selector-wrapper">
          <CategorySelector
            hideTitle={true}
            mainCategoryLabel={intl.formatMessage({
              id: 'form.categories_title',
            })}
            mainCategoryValue={filterCategory['main-category'] ?? undefined}
            subCategoryValue={filterCategory['sub-category'] ?? undefined}
            subSubCategoryValue={filterCategory['sub-subcategory'] ?? undefined}
            availableOptions={availableOptions || undefined}
          />
        </div>

        <StatusCheckboxes
          public
          availableOptions={availableOptions?.statuses}
        />

        <LocationRadiusSelectorWithAmk
          locations={filterOptions.locations_radius}
          form={form}
          name="location"
          latName="lks_lat"
          longName="lks_long"
          onChange={() => setFilter(form.getFieldsValue())}
        />

        <Button
          label={intl.formatMessage({ id: 'general.delete_filters' })}
          onClick={handleClearAllFilters}
          type="default"
          className="clear-filters-button"
        />
      </Form>
    </StyledListingsFilters>
  );
};
